// Duration parsing/formatting in token format: d (days), h (hours), m (minutes).
// 1d = 8h = 480m. Round-trips with the DurationInput component.

export const MIN_PER_HOUR = 60;
export const MIN_PER_DAY = 480;

const UNIT_MINUTES: Record<string, number> = {
  d: MIN_PER_DAY,
  h: MIN_PER_HOUR,
  m: 1,
};

const TOKEN_RE = /(\d*\.?\d+)\s*([dhm])/g;

// Parse user string -> total minutes, or null if empty/invalid.
export function parseDuration(input: string): number | null {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;

  const seen = new Set<string>();
  let total = 0;
  let consumed = "";

  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(normalized)) !== null) {
    const [token, amount, unit] = match;
    if (seen.has(unit)) return null; // unit used more than once
    seen.add(unit);
    total += parseFloat(amount) * UNIT_MINUTES[unit];
    consumed += token;
  }

  if (seen.size === 0) return null; // no valid tokens

  // Reject leftover junk (unknown tokens, malformed input).
  if (normalized.replace(/\s/g, "") !== consumed.replace(/\s/g, "")) {
    return null;
  }

  return Math.round(total);
}

// Format minutes -> human-readable token string, e.g. 510 -> "1d 30m".
export function formatDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return "0m";

  const days = Math.floor(minutes / MIN_PER_DAY);
  const rem = minutes % MIN_PER_DAY;
  const hours = Math.floor(rem / MIN_PER_HOUR);
  const mins = rem % MIN_PER_HOUR;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins) parts.push(`${mins}m`);

  return parts.join(" ");
}

// Lenient parse for time entry: plain number = minutes, else token format.
export function parseMinutes(input: string): number | null {
  const t = input.trim();
  if (/^\d+$/.test(t)) return Number(t) || null;
  return parseDuration(t);
}

// Minutes -> "1h 30m" (no day rollup; reads better for logged/estimated work).
export function formatHoursMinutes(minutes: number): string {
  const h = Math.floor(minutes / MIN_PER_HOUR);
  const m = minutes % MIN_PER_HOUR;
  return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(" ");
}
